/**
 * Product Meta Specs Component
 * Handles tooltip interactions and clearance product logic
 */

class ProductMetaSpecs {
  constructor() {
    this.tooltips = [];
    this.init();
  }

  /**
   * Initialize the component
   */
  init() {
    if (document.readyState === 'loading') {
      document.addEventListener('DOMContentLoaded', () => {
        this.setupTooltips();
        this.handleClearanceProducts();
      });
    } else {
      this.setupTooltips();
      this.handleClearanceProducts();
    }
  }

  /**
   * Setup tooltip functionality
   */
  setupTooltips() {
    const tooltipElements = document.querySelectorAll('.tooltip');
    
    if (!tooltipElements.length) return;

    tooltipElements.forEach((tooltip) => {
      this.initializeTooltip(tooltip);
    });

    // Global click handler to close tooltips
    this.setupGlobalClickHandler();
  }

  /**
   * Initialize individual tooltip
   * @param {HTMLElement} tooltip 
   */
  initializeTooltip(tooltip) {
    const content = tooltip.querySelector('.tooltip__content');
    if (!content) return;

    const tooltipData = {
      element: tooltip,
      content: content,
      isActive: false
    };

    // Mouse events
    tooltip.addEventListener('mouseenter', () => this.showTooltip(tooltipData));
    tooltip.addEventListener('mouseleave', () => this.hideTooltip(tooltipData));

    // Touch events for mobile
    tooltip.addEventListener('touchstart', (e) => {
      e.preventDefault();
      this.toggleTooltip(tooltipData);
    });

    // Keyboard accessibility
    tooltip.addEventListener('keydown', (e) => {
      if (e.key === 'Enter' || e.key === ' ') {
        e.preventDefault();
        this.toggleTooltip(tooltipData);
      } else if (e.key === 'Escape') {
        this.hideTooltip(tooltipData);
      }
    });

    this.tooltips.push(tooltipData);
  }

  /**
   * Show tooltip
   * @param {Object} tooltipData 
   */
  showTooltip(tooltipData) {
    if (tooltipData.isActive) return;

    tooltipData.content.style.visibility = 'visible';
    tooltipData.content.style.opacity = '1';
    tooltipData.element.classList.add('is-active');
    tooltipData.isActive = true;

    // Set ARIA attributes for accessibility
    tooltipData.content.setAttribute('aria-hidden', 'false');
  }

  /**
   * Hide tooltip
   * @param {Object} tooltipData 
   */
  hideTooltip(tooltipData) {
    if (!tooltipData.isActive) return;

    tooltipData.content.style.visibility = 'hidden';
    tooltipData.content.style.opacity = '0';
    tooltipData.element.classList.remove('is-active');
    tooltipData.isActive = false;

    // Set ARIA attributes for accessibility
    tooltipData.content.setAttribute('aria-hidden', 'true');
  }

  /**
   * Toggle tooltip state
   * @param {Object} tooltipData 
   */
  toggleTooltip(tooltipData) {
    if (tooltipData.isActive) {
      this.hideTooltip(tooltipData);
    } else {
      // Hide other tooltips first
      this.hideAllTooltips();
      this.showTooltip(tooltipData);
    }
  }

  /**
   * Hide all active tooltips
   */
  hideAllTooltips() {
    this.tooltips.forEach(tooltip => {
      if (tooltip.isActive) {
        this.hideTooltip(tooltip);
      }
    });
  }

  /**
   * Setup global click handler to close tooltips when clicking outside
   */
  setupGlobalClickHandler() {
    document.addEventListener('click', (event) => {
      const isTooltipClick = event.target.closest('.tooltip');
      
      if (!isTooltipClick) {
        this.hideAllTooltips();
      }
    });
  }

  /**
   * Handle clearance product specific logic
   */
  handleClearanceProducts() {
    try {
      const currentUrl = window.location.href;

      if (!currentUrl.includes("/clearance/products/")) {
        console.log("This page is a clearance product page.");
        const hurryElements = document.querySelectorAll('.card__mark__hurry');
        
        hurryElements.forEach(element => {
          if (element) {
            element.style.display = 'none';
          }
        });
      }
    } catch (error) {
      console.warn('Error handling clearance products:', error);
    }
  }

  /**
   * Destroy the component and clean up event listeners
   */
  destroy() {
    this.tooltips.forEach(tooltip => {
      // Remove event listeners would need to be implemented
      // if we stored references to the handlers
    });
    this.tooltips = [];
  }
}

// Initialize the component
const productMetaSpecs = new ProductMetaSpecs();

// Export for potential use in other modules
if (typeof module !== 'undefined' && module.exports) {
  module.exports = ProductMetaSpecs;
} 